import React, { useState, useEffect, useRef } from 'react'
import AuthLayout from '../../../../layout/AuthLayout'
import { Box, Flex, Text, Group, TextInput, Button, Pagination, Select, Modal, ScrollArea } from '@mantine/core'
import classes from '../../../../assets/module-css/AdminLayout.module.css'
import { useDispatch } from 'react-redux'
import { updateRoute } from '../../../../store/dataRoute'
import { getAllProvinceList, deleteProvince, getAllCountryList } from '../../../../services/location'
import { useDebouncedCallback } from '@mantine/hooks'
import LoadingData from '../../../../component/ui/LoadingData'
import NoData from '../../../../component/ui/NoData'
import TableProvince from '../../../../component/pages/Province/TableProvince'
import FormProvince from '../../../../component/pages/Province/FormProvince'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX, IconPlus } from '@tabler/icons-react'
import { modals } from '@mantine/modals'

const defaultParameter = {
    'page[num]': 1,
    'page[limit]': 10,
    sort: '-id',
    include: 'countries'
}

const labelTable = [
    { label: 'ID', width: 80 },
    { label: 'Province Name', width: 'auto' },
    { label: 'Country', width: 250 },
    { label: 'Action', width: 100 },
]

const ProvinceList = () => {
    const dispatch = useDispatch()
    const [params, setParams] = useState(defaultParameter)
    const [provinceList, setProvinceList] = useState([])
    const [totalData, setTotalData] = useState(0)
    const [loading, setLoading] = useState(true)
    const [openModalForm, setOpenModalForm] = useState(false)
    const [detailData, setDetailData] = useState(null)
    const [allCountries, setAllCountries] = useState([])
    const countriesRef = useRef([])

    const mappingRoute = [
        { label: '/', route: null },
        { label: 'Settings', route: null },
        { label: 'Province List', route: null },
    ]

    const handleGetAllProvinceList = useDebouncedCallback(async () => {
        setLoading(true)
        try {
            const response = await getAllProvinceList(params)

            if (response.success || response.data) { // Handle both success field or direct data
                const startData = response.data || []
                const included = response.included || []
                const countData = response.meta?.pagination?.total || 0

                const remapProvince = startData.map((val) => {
                    let countryId = null

                    // 1. Try to find country ID from relationship
                    if (val.relationships?.country?.id) {
                        countryId = val.relationships.country.id
                    }
                    // 2. Or from attributes
                    else if (val.attributes?.country_id) {
                        countryId = val.attributes.country_id
                    }

                    return {
                        id: val.id,
                        name: val.attributes.name,
                        country_id: countryId,
                        country_name: included.countries[countryId]?.attributes?.name || "---",
                    }
                })

                setProvinceList(remapProvince)
                setTotalData(countData)
            } else {
                setProvinceList([])
                setTotalData(0)
            }
        } catch (error) {
            console.log(error)
            setProvinceList([])
        } finally {
            setLoading(false)
        }
    }, 500)

    useEffect(() => {
        dispatch(updateRoute({ 'data': mappingRoute }))

        const fetchCountries = async () => {
            try {
                const response = await getAllCountryList({ 'page[limit]': 300 })
                if (response && response.data) {
                    const mapped = response.data.map(c => ({
                        value: String(c.attributes.id),
                        label: c.attributes.name
                    }))
                    setAllCountries(mapped)
                    countriesRef.current = response.data
                }
            } catch (error) {
                console.error("Failed to fetch countries for mapping", error)
            }
        }
        fetchCountries()
        // eslint-disable-next-line 
    }, [dispatch])

    useEffect(() => {
        handleGetAllProvinceList()
        // eslint-disable-next-line
    }, [params])

    const handleDelete = (val) => {
        modals.openConfirmModal({
            title: 'Delete Province',
            children: (
                <Text size="sm">
                    Are you sure you want to delete province <strong>{val.name}</strong>? This action cannot be undone.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onCancel: () => console.log('Cancel'),
            onConfirm: async () => {
                try {
                    const response = await deleteProvince(val.id)
                    if (response.success) {
                        notifications.show({
                            title: 'Success',
                            message: 'Province deleted successfully',
                            color: 'teal',
                            icon: <IconCheck />,
                        })
                        handleGetAllProvinceList()
                    } else {
                        notifications.show({
                            title: 'Failed',
                            message: response.message || 'Failed to delete province',
                            color: 'red',
                            icon: <IconX />,
                        })
                    }
                } catch (error) {
                    notifications.show({
                        title: 'Error',
                        message: error.message || 'Something went wrong',
                        color: 'red',
                        icon: <IconX />,
                    })
                }
            },
        })
    }

    const actions = {
        edit: (val) => {
            setDetailData(val)
            setOpenModalForm(true)
        },
        delete: (val) => {
            handleDelete(val)
        }
    }

    const handleAction = (val, type) => {
        if (actions[type]) {
            actions[type](val)
        }
    }

    const handleChangeFilter = (name, val) => {
        setParams((oldVal) => ({ ...oldVal, [name]: val }))
    }

    const handleChangePage = (val) => {
        setParams((oldVal) => ({ ...oldVal, ['page[num]']: val }))
    }

    const onCloseModalForm = () => {
        setOpenModalForm(false)
        setDetailData(null)
    }

    const handleAdd = () => {
        setDetailData(null)
        setOpenModalForm(true)
    }

    return (
        <AuthLayout>
            <Box my={20}>
                <Box>
                    <Flex justify='space-between' align="center" mb={30}>
                        <Text className={classes.titleLayout}>Province List</Text>
                        <Group gap='sm' align='center'>
                            <Select
                                placeholder="Filter Country"
                                data={allCountries}
                                value={params['filter[country_id]'] || null}
                                onChange={(val) => {
                                    handleChangeFilter('filter[country_id]', val)
                                    handleChangePage(1)
                                }}
                                searchable
                                clearable
                                size='xs'
                                w={200}
                            />
                            <TextInput
                                value={params['filter[name][like]'] || ''}
                                placeholder='Search by name'
                                onChange={(val) => {
                                    handleChangeFilter('filter[name][like]', val.target.value)
                                    handleChangePage(1)
                                }}
                                size='xs'
                            />
                            <Button size='xs' leftSection={<IconPlus size={14} />} onClick={handleAdd}>Add Province</Button>
                        </Group>
                    </Flex>
                </Box>

                <Box my={30}>
                    <Group mb={10} gap='xs'>
                        <Text fz={12}>Total Data Found: </Text>
                        <Text fz={12} fw='bold'>{totalData} Provinces </Text>
                    </Group>
                    {loading ? <LoadingData /> : provinceList.length > 0 ? <TableProvince label={labelTable} data={provinceList} actionMethod={handleAction} /> : <NoData />}
                </Box>
                <Flex justify='end'>
                    <Pagination size='sm' onChange={handleChangePage} value={params['page[num]']} total={totalData > 0 ? Math.ceil(totalData / params['page[limit]']) : 0} />
                </Flex>
            </Box>

            <Modal opened={openModalForm} onClose={onCloseModalForm} centered closeOnClickOutside={false} size='md' title={<Text fw='Bold'>{detailData ? 'Edit Province' : 'Add Province'}</Text>} scrollAreaComponent={ScrollArea.Autosize} withCloseButton={false}>
                <FormProvince dataDetail={detailData} onCloseModal={onCloseModalForm} reloadList={handleGetAllProvinceList} />
            </Modal>
        </AuthLayout>
    )
}

export default ProvinceList
